import {
  GOOGLE_INTEGRATIONS_TITLE,
  OUTLOOK_INTEGRATIONS_TITLE,
  ZENDESK_INTEGRATIONS_TITLE,
  ZOOM_TITLE,
} from "@tellescope/constants";
import { useTranslation } from "react-i18next";

/**
 * Centralizes the editor's display strings as literal `t()` calls (keyed by the stable config keys),
 * since the project's i18n lint rule forbids `t(variable)`.
 */
export const useRoleEditorLabels = () => {
  const { t } = useTranslation("settings");

  const features: Record<string, string> = {
    contacts: t("Contacts"),
    contactViews: t("Contact Views"),
    users: t("Users"),
    organization: t("Organization"),
    tickets: t("Tickets"),
    calendarEvents: t("Calendar Events"),
    calendarEventLocations: t("Calendar Event Locations"),
    calendarEventTemplates: t("Calendar Event Templates"),
    calendarBookingPages: t("Calendar Booking Pages"),
    databases: t("Databases"),
    databaseEntries: t("Database Entries"),
    forms: t("Forms"),
    formQuestions: t("Form Questions"),
    formResponses: t("Form Responses"),
    files: t("Files"),
    faxes: t("Faxes"),
    journeys: t("Journeys"),
    triggers: t("Triggers"),
    templates: t("Templates"),
    community: t("Community"),
    content: t("Content"),
    contentAssignment: t("Content Assignment"),
    integrations: t("Integrations"),
    backgroundErrors: t("Background Error Notifications"),
    smsMessages: t("SMS Messages"),
    emails: t("Emails"),
    phoneCalls: t("Phone Calls"),
    purchases: t("Purchases"),
    chatRooms: t("Chat Rooms"),
    analytics: t("Analytics"),
    engagementEvents: t("Engagement Events"),
    notes: t("Notes (Unstructured)"),
    medications: t("Medications"),
    vitalsConfigurations: t("Vitals Configurations"),
    portalCustomizations: t("Portal Customizations"),
    phoneTrees: t("Phone Trees"),
    tableViews: t("Table Views (Tickets, Inbox)"),
    customDashboards: t("Custom Dashboards"),
    apiKeys: t("API Keys"),
    timeTracks: t("Time Tracks"),
  };

  const accessLevels: Record<string, string> = {
    All: t("Full Access"),
    Assigned: t("Assigned"),
    Default: t("Shared"),
    none: t("No Access"),
  };

  const toggles: Record<string, string> = {
    hideDashboard: t("Hide Dashboard Page"),
    hideInbox: t("Hide Inbox Page"),
    hideTeamChat: t("Hide Team Chat Page"),
    disableTicketDueDate: t("Disable Editing Ticket Due Date"),
    disableUnstructuredNotes: t("Disable Unstructured Notes (Input on Profile)"),
    hideEnduserChat: t("Hide Contact Chat"),
    hideEnduserNote: t("Hide Contact Note"),
    hideCareplan: t("Hide care plan tab from profile view"),
    hideUnsubmittedForms: t("Hide unsubmitted contact form responses from profile timeline"),
    hideMergeEndusers: t("Hide merge contacts functionality"),
    hideQueuedTicketsViewer: t("Hide ability to view all tickets in queue"),
    hideIncomingFaxesIcon: t("Hide incoming faxes icon"),
    hideNotificationsIcon: t("Hide notifications icon"),
    hideBulkEnduserActions: t("Hide bulk actions on Contacts list"),
    hideViewPortalAsEnduser: t('Hide "View portal as contact" button'),
    hideCalendarUserSelector: t("Hide Calendar 'View Other Calendars' selector"),
    hideCalendarSavedViews: t("Hide Calendar saved views selector"),
    hideCalendarFilters: t("Hide Calendar filters"),
    disableTimeTrackApproval: t("Disable Time Track Approval"),
  };

  const portalSchemaRestrictions: Record<string, string> = {
    disableEditContent: t("Disable editing Portal content"),
    disableEditTheming: t("Disable editing Portal theming"),
    disableEditSnippets: t("Disable editing Portal snippets"),
  };

  const redactions: Record<string, string> = {
    recordings: t("Redact voicemail/call recordings"),
    voicemailTranscription: t("Redact voicemail transcription"),
    callTranscription: t("Redact call transcription"),
    callSummary: t("Redact call transcription summary"),
  };

  const integrations: Record<string, string> = {
    "Google Calendar": t("Google Calendar"),
    [GOOGLE_INTEGRATIONS_TITLE]: t("Google integrations"),
    [OUTLOOK_INTEGRATIONS_TITLE]: t("Outlook integrations"),
    [ZENDESK_INTEGRATIONS_TITLE]: t("Zendesk integrations"),
    [ZOOM_TITLE]: t("Zoom"),
  };

  return { features, accessLevels, toggles, redactions, integrations, portalSchemaRestrictions };
};
